import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CreditCard, Loader2, ExternalLink, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useAccount } from "@/context/AccountContext";
import { startCheckout, openBillingPortal } from "@/lib/api";
import { CardSkeleton } from "@/components/Skeletons";

const PLANS = [
  { tier: "free", name: "Free", price: "£0", period: "forever", blurb: "For the occasional check.", features: ["1 image check per day", "Metadata + AI detection report", "Case history"] },
  { tier: "plus", name: "Plus", price: "£6.99", period: "/ month", blurb: "For a working picture desk.", features: ["Unlimited image checks", "No daily limit, ever", "Everything in Free"], accent: "sky" },
  { tier: "pro", name: "Pro", price: "£29", period: "/ month", blurb: "For newsrooms and agencies.", features: ["Unlimited image checks", "Batch upload", "Priority processing"], accent: "emerald" },
];

const ACCENT = {
  sky: { ring: "border-sky-500/40", text: "text-sky-300", btn: "bg-sky-400 hover:bg-sky-300 text-[#0A0A0B]" },
  emerald: { ring: "border-emerald-500/40", text: "text-emerald-300", btn: "bg-emerald-400 hover:bg-emerald-300 text-[#0A0A0B]" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function Account() {
  const { account, error, loading, refresh } = useAccount();
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  // Back from Stripe Checkout: the webhook updates the tier asynchronously, so poll briefly.
  useEffect(() => {
    const state = params.get("checkout");
    if (!state) return;
    if (state === "success") {
      toast.success("Payment received", { description: "Activating your plan…" });
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        const a = await refresh();
        if ((a && a.effective_tier !== "free") || tries >= 10) clearInterval(timer);
      }, 2000);
      setParams({}, { replace: true });
      return () => clearInterval(timer);
    }
    if (state === "cancelled") {
      toast.message("Checkout cancelled", { description: "No changes were made to your plan." });
      setParams({}, { replace: true });
    }
  }, [params, refresh, setParams]);

  const choose = async (tier) => {
    setBusy(tier);
    try {
      const res = await startCheckout(tier);
      if (res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      if (res.changed) {
        toast.success("Plan updated", { description: "Stripe is applying the change." });
        setTimeout(refresh, 2500);
      } else if (res.already_on_tier) {
        toast.message("You're already on this plan.");
      }
    } catch (e) {
      toast.error("Could not start checkout", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { url } = await openBillingPortal();
      window.location.assign(url);
    } catch (e) {
      toast.error("Could not open billing portal", { description: e.message });
      setBusy(null);
    }
  };

  const current = account?.effective_tier || "free";
  const usage = account?.usage;

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="account-page">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Account</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Plan & usage</h1>
          <p className="text-sm text-zinc-400 mt-2">
            {current === "free" ? "Free accounts get one check a day, resetting at midnight UTC." : "Unlimited checks on your plan. No daily limit."}
          </p>
        </div>
        <button onClick={refresh} className="inline-flex items-center gap-2 rounded-lg border border-[#272A30] px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-[#14171D]" data-testid="account-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {error && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">{error}</div>
      )}

      {account?.billing_mode === "test" && account?.billing_enabled && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs px-4 py-2 inline-flex items-center gap-2" data-testid="test-mode-banner">
          <AlertTriangle className="h-3.5 w-3.5" /> Stripe is in test mode. Use card 4242 4242 4242 4242, any future expiry, any CVC.
        </div>
      )}

      {!account ? (
        <section className="grid sm:grid-cols-3 gap-4"><CardSkeleton /><CardSkeleton /><CardSkeleton /></section>
      ) : (
        <section className="grid sm:grid-cols-3 gap-4" data-testid="account-summary">
          <Stat label="Current plan" value={account.tier_label} hint={account.subscription_status ? `Stripe: ${account.subscription_status}` : "No subscription"} />
          <Stat label="Checks today" value={usage?.unlimited ? `${usage.used_today}` : `${usage?.used_today ?? 0} / ${usage?.daily_limit ?? 1}`} hint={usage?.admin_exempt ? "Admin account, no limits" : usage?.unlimited ? "Unlimited on this plan" : `${usage?.remaining_today ?? 0} remaining`} />
          <Stat label="Member since" value={fmtDate(account.signup_date)} hint={account.current_period_end ? `Renews ${fmtDate(account.current_period_end)}` : account.email} />
        </section>
      )}

      <section className="grid md:grid-cols-3 gap-4" data-testid="plan-grid">
        {PLANS.map((p) => {
          const a = p.accent ? ACCENT[p.accent] : null;
          const isCurrent = current === p.tier;
          const disabled = !account || !account.billing_enabled || busy !== null;
          return (
            <div key={p.tier} data-testid={`plan-${p.tier}`} className={`rounded-xl border bg-[#111318] p-5 flex flex-col ${isCurrent ? (a?.ring || "border-zinc-400/50") : "border-[#272A30]"}`}>
              <div className="flex items-baseline justify-between">
                <div className={`text-lg font-semibold ${a?.text || "text-zinc-100"}`}>{p.name}</div>
                {isCurrent && <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Current</span>}
              </div>
              <div className="mt-2"><span className="text-3xl font-semibold tracking-tight">{p.price}</span><span className="text-sm text-zinc-500 ml-1">{p.period}</span></div>
              <p className="text-xs text-zinc-500 mt-1">{p.blurb}</p>
              <ul className="mt-4 space-y-2 text-sm text-zinc-300 flex-1">
                {p.features.map((f) => (<li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-zinc-500" />{f}</li>))}
              </ul>
              <div className="mt-5">
                {p.tier === "free" ? (
                  isCurrent ? <span className="text-xs text-zinc-500">Your current plan</span> : (
                    <button onClick={manage} disabled={disabled} className="w-full rounded-lg border border-[#272A30] px-3 py-2 text-sm text-zinc-300 hover:bg-[#1A1D24] disabled:opacity-50">Cancel in billing portal</button>
                  )
                ) : isCurrent ? (
                  <button onClick={manage} disabled={disabled} data-testid={`manage-${p.tier}`} className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#272A30] px-3 py-2 text-sm text-zinc-300 hover:bg-[#1A1D24] disabled:opacity-50">
                    {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Manage billing
                  </button>
                ) : (
                  <button onClick={() => choose(p.tier)} disabled={disabled} data-testid={`choose-${p.tier}`} className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50 ${a.btn}`}>
                    {busy === p.tier ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                    {current === "free" ? `Upgrade to ${p.name}` : `Switch to ${p.name}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {account && !account.billing_enabled && (
        <p className="text-xs text-zinc-500" data-testid="billing-disabled-note">Billing isn't switched on for this environment yet, so plan buttons are disabled.</p>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-[#272A30] bg-[#111318] p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-3 text-2xl font-semibold tracking-tight tabular-nums truncate">{value}</div>
      <div className="mt-1 text-xs text-zinc-500 truncate">{hint}</div>
    </div>
  );
}
